import { CrmApiRepository } from './crm/CrmApiRepository.js';
import { RevenueSchedulesApiService } from './crm/RevenueSchedulesApiService.js';
import { mapScheduleFromApi, mapScheduleToApi } from './crm/ApiEntityMapper.js';
import { nowIso } from '../utils/IdUtils.js';
import { CommissionScheduleService } from './CommissionScheduleService.js';
import { RevenueCalculationService } from './RevenueCalculationService.js';
import { AuditService } from './AuditService.js';

export class RevenueScheduleService {
    static async getByDeal(dealId) {
        return CrmApiRepository.getSchedulesByDeal(dealId);
    }

    static async getAll() {
        return CrmApiRepository.getAllSchedules();
    }

    static async updateSchedule(dealId, scheduleId, payload) {
        const updated = await RevenueSchedulesApiService.update(dealId, scheduleId, payload);
        return mapScheduleFromApi(updated);
    }

    static async generateForDeal(deal, accountName) {
        let schedules = [];
        const model = deal.paymentModel || 'monthly';
        if (model === 'milestone' || model === 'custom') {
            schedules = RevenueCalculationService.generateScheduleForDeal(deal);
        } else if (model === 'ongoing') {
            schedules = RevenueCalculationService.generateScheduleForDeal(deal);
        } else if (deal.expectedFirstInvoiceDate && deal.lastInvoiceDate && deal.invoiceAmount > 0) {
            schedules = RevenueCalculationService.generateScheduleForDeal(deal);
        } else {
            const contractValue = deal.contractValue || deal.dealAmount || deal.amount || 0;
            const closeDate = deal.actualCloseDate || deal.expectedCloseDate || nowIso().split('T')[0];
            if (contractValue > 0) {
                schedules = RevenueCalculationService.generateSchedule({
                    ...deal,
                    id: deal.id,
                    expectedFirstInvoiceDate: closeDate,
                    lastInvoiceDate: closeDate,
                    invoiceFrequency: 'oneTime',
                    invoiceAmount: contractValue
                });
            }
        }

        const created = [];
        for (const s of schedules) {
            const payload = mapScheduleToApi({
                ...s,
                scheduleName: s.milestoneTitle || s.scheduleName || `${accountName || 'Deal'} invoice`
            });
            const row = await RevenueSchedulesApiService.create(deal.id, payload);
            created.push(mapScheduleFromApi(row));
        }

        await AuditService.log({
            entityType: 'deal',
            entityId: deal.id,
            action: 'schedule.generated',
            after: { count: created.length }
        });
        return created;
    }

    static async markIssued(scheduleId) {
        const updated = await RevenueSchedulesApiService.updateStatus(scheduleId, { status: 'issued' });
        return mapScheduleFromApi(updated);
    }

    static async markPaid(scheduleId, paidAmount = null) {
        const schedules = await CrmApiRepository.getAllSchedules();
        const row = schedules.find(s => s.id === scheduleId);
        if (!row) return null;
        const amount = paidAmount ?? row.plannedAmount ?? 0;
        const updated = await RevenueSchedulesApiService.updateStatus(scheduleId, {
            status: 'paid',
            paid_amount: amount
        });
        const mapped = mapScheduleFromApi(updated) || { ...row, status: 'paid', paidAmount: amount, actualAmount: amount };
        await CommissionScheduleService.accrueOnInvoicePaid(mapped);
        await AuditService.log({
            entityType: 'schedule',
            entityId: scheduleId,
            action: 'schedule.paid',
            before: { status: row.status },
            after: { status: 'paid', paidAmount: amount }
        });
        window.dispatchEvent(new CustomEvent('invoicePaid', { detail: { schedule: mapped } }));
        return mapped;
    }

    static async markOverdue(scheduleId) {
        const updated = await RevenueSchedulesApiService.updateStatus(scheduleId, { status: 'overdue' });
        return mapScheduleFromApi(updated);
    }

    static async cancelSchedule(scheduleId, reason = '') {
        const updated = await RevenueSchedulesApiService.updateStatus(scheduleId, {
            status: 'cancelled',
            cancel_reason: reason || null
        });
        const mapped = mapScheduleFromApi(updated);
        await AuditService.log({
            entityType: 'schedule',
            entityId: scheduleId,
            action: 'schedule.cancelled',
            before: { status: 'planned' },
            after: { status: 'cancelled', cancelReason: reason }
        });
        window.dispatchEvent(new CustomEvent('invoiceCancelled', { detail: { schedule: mapped } }));
        return mapped;
    }

    static async linkZohoInvoice(dealId, scheduleId, lookup) {
        const raw = String(lookup || '').trim();
        if (!raw) throw new Error('Enter a Zoho invoice ID or invoice number');
        const payload = /^\d+$/.test(raw) ? { invoice_id: raw } : { invoice_number: raw };
        const updated = await RevenueSchedulesApiService.linkZohoInvoice(dealId, scheduleId, payload);
        CrmApiRepository.clearCache();
        return mapScheduleFromApi(updated);
    }

    static async unlinkZohoInvoice(dealId, scheduleId) {
        const updated = await RevenueSchedulesApiService.unlinkZohoInvoice(dealId, scheduleId);
        CrmApiRepository.clearCache();
        return mapScheduleFromApi(updated);
    }
}
